import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  Badge,
  badgeStyle,
  badgeType,
  colors,
  fontWeight,
  Heading,
  headingLevel,
  iconBtnType,
  IconButton,
  Icons,
} from '../../../../../packages';
import { useDomainType } from '../../../../../hooks/useDomainType';
import { createNewAssociatedTag } from '../../../../../services/member/member.service';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { DomainTypesEnum } from '../../../../../constants/CommonConstants';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import PriorityEmpty from '../PriorityEmpty';
import AddNewReport from '../AddNewReport';
import ViewDetail from '../Diagnoses/diagnoses-detail';
import EditItem from '../Diagnoses/diagnoses-editing';
import LifeEventCard from './life-event-card';
import { stepTitles } from './LifeEvent.constants';
import { useStyles } from '../../MemberDetail.styles';

const LifeEvents = () => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const { memberId } = useParams();
  const { meta } = useSelector(getAuth);
  const { profile, selectedPatient } = useSelector(getProfile);
  const [isLoading, domainData, domainOptions] = useDomainType({
    type: DomainTypesEnum.LIFE_EVENTS,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errorApi, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddDrawer, setShowAddDrawer] = useState(false);

  const onSelectedItem = (item, isEdit = false) => {
    setSelectedItem(item);
    setIsEditing(isEdit);
  };

  const onCloseDrawer = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowAddDrawer(false);
  };

  const onAddNewLifeEvents = () => {
    setShowAddDrawer(true);
  };

  const onUpdateItem = async data => {
    setIsSaving(true);
    setError(null);
    try {
      await createNewAssociatedTag({
        patientId: memberId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: selectedItem.domainElementId,
            metaData: {
              notes: data.notes,
              interferenceInLife: data.isInterference,
              selectedImportance: data.status,
            },
          },
        ],
      });
      setIsSaving(false);
      dispatch(profileActionCreators.fetchDomainTypes(memberId));
      onCloseDrawer();
    } catch (error) {
      setError(error.data.message || error.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onSubmitNewReport = payload => {
    const data = {
      patientId: memberId,
      tagItems: payload.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          interferenceInLife: item.isInterference,
          notes: item.notes,
          selectedImportance: item.status,
        },
      })),
    };
    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: errorMsg => {
          if (!errorMsg) {
            onCloseDrawer();
          }
        },
      }),
    );
  };

  return (
    <>
      <AddNewReport
        isOpen={showAddDrawer}
        onClose={onCloseDrawer}
        title="Report new life events"
        stepTitles={stepTitles}
        isLifeEvent
        showInterference={false}
        searchLabel="Select one or more life events"
        options={domainOptions}
        onSubmit={onSubmitNewReport}
      />
      <ViewDetail
        open={!!selectedItem && !isEditing}
        onClose={onCloseDrawer}
        isLifeEvent
        onClickNewReport={onAddNewLifeEvents}
        diagnoses={selectedItem}
      />
      <EditItem
        title="Update life event"
        isOpen={!!selectedItem && isEditing}
        onClose={onCloseDrawer}
        isLifeEvent
        item={selectedItem}
        onSubmit={onUpdateItem}
        error={errorApi}
        isSaving={isSaving}
        options={domainOptions}
        type={DomainTypesEnum.LIFE_EVENTS}
      />
      <div className={classes.headingWithBtnWrapper}>
        <div className={classes.noteHeaderLeftSide}>
          <Heading className={classes.heading} level={headingLevel.XL} weight={fontWeight.BOLD}>
            Life events
          </Heading>
          <Badge variant={badgeType.OUTLINED} style={badgeStyle.UNRELATED} className={classes.totalBadge}>
            {domainData?.relatedElements.length} total
          </Badge>
        </div>
        {domainData?.relatedElements.length > 0 && (
          <IconButton
            className={classes.addBtn}
            icon="plus"
            variant={iconBtnType.PRIMARY}
            onClick={onAddNewLifeEvents}
          >
            Add life event
          </IconButton>
        )}
      </div>
      <div className={clsx(classes.noteCardWrapper)}>
        {isLoading ? (
          <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
        ) : (
          <>
            {domainData?.relatedElements.length > 0 ? (
              <>
                {domainData.relatedElements.map(item => (
                  <LifeEventCard item={item} key={item.id} onClick={onSelectedItem} showNotes={false} />
                ))}
              </>
            ) : (
              <PriorityEmpty
                title={`No Life Events reported for ${selectedPatient.member.nickName} yet.`}
                // eslint-disable-next-line max-len
                description="If you've spoken with them about something that's recent happened that the team should know about it, record it here."
                addText="Add life event"
                onAdd={onAddNewLifeEvents}
                glyph="face-talking-celebrating"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export { LifeEvents };
